import { Proposal, PricingDetail } from '@/types'
import dayjs from 'dayjs'
import logoBase64 from '@/assests/shakthiLogo'

interface ProposalPdfData {
  proposal: Proposal
  customerName: string
  customerAddress: string
  customerCity: string
  /**
   * Rich-text body resolved from the chosen (or default) Proposal Template.
   * When provided, it replaces the hardcoded intro + terms block, with
   * placeholders ({{PRICING_TABLE}}, {{validityDate}}, …) substituted below.
   */
  templateBodyHtml?: string
  /** Dynamic cover page (page 1) resolved from the template — null/undefined skips it. */
  coverPage?: { title: string; subtitle: string; description?: string; image?: string } | null
  /** Company Content page (page 2, rich text w/ images) — empty string skips it. */
  companyContentHtml?: string
  /** Terms & Conditions (rich text), appended after the letter body — empty string skips it. */
  termsConditionsHtml?: string
  /** Name of the employee who prepared / created this proposal. */
  preparedBy?: string
}

export const generateProposalHTML = (data: ProposalPdfData): string => {
  const {
    proposal,
    customerName,
    customerAddress,
    customerCity,
    templateBodyHtml,
    coverPage,
    companyContentHtml,
    termsConditionsHtml,
    preparedBy,
  } = data

  const offerDate = proposal.offerDate
    ? dayjs(proposal.offerDate).format('DD-MM-YYYY')
    : dayjs().format('DD-MM-YYYY')

  const validityDate = dayjs().add(proposal.validity, 'day').format('DD-MM-YYYY')

  const FONT = `font-family:'Inter','Segoe UI',Arial,sans-serif`
  const pricingTableHtml = (proposal.pricingDetails || [])
    .map(
      (row: PricingDetail) => `
      <tr>
        <td style="border:1px solid #000;padding:8px 10px;text-align:center;background:#fff;font-weight:600;${FONT}">${row.supplySource || ''}</td>
        <td style="border:1px solid #000;padding:8px 10px;text-align:center;background:#fff;font-weight:600;${FONT}">${row.volumePerAnnum || ''}</td>
        <td style="border:1px solid #000;padding:8px 10px;text-align:center;background:#fff;font-weight:600;${FONT}">${row.drawlVoltage || ''}</td>
        <td style="border:1px solid #000;padding:8px 10px;text-align:center;background:#fff;font-weight:600;${FONT}">Rs ${(row.rate || 0).toFixed(2)}/-</td>
        <td style="border:1px solid #000;padding:8px 10px;text-align:center;background:#fff;font-weight:600;${FONT}">${row.equityInvestment || 'NIL'}</td>
        <td style="border:1px solid #000;padding:8px 10px;text-align:center;background:#fff;font-weight:600;${FONT}">${row.securityMechanism || 'Nil'}</td>
      </tr>`
    )
    .join('')

  const pricingTableOnly = `
  <table class="pricing">
    <thead>
      <tr>
        <th>Type of Power</th>
        <th>Volume in units per annum</th>
        <th>Drawl Voltage</th>
        <th>Rate *</th>
        <th>Equity Investment</th>
        <th>Security Mechanism</th>
      </tr>
    </thead>
    <tbody>${pricingTableHtml}</tbody>
  </table>`

  // Default body (used when no template body is supplied) — the original letter.
  const defaultBody = `
  <div class="intro">
    <strong>Sir,</strong><br><br>
    Shakthi Power is a successful Power Consulting Company in Tamil Nadu with focus on "Third Party Power and Group Captive Power". Shakthi Power offers 250 kWh to any volume in single window turnkey solutions on Biomass, Coal, Solar &amp; Wind Energy. With more than 10 years of experience and proficient workforce, we at Shakthi Power excel in complete turnkey in Power Trading enabling a smooth transaction with the Customers, Generators and TANGEDCO.<br><br>
    The core competencies of Shakthi Power primarily include Customer Satisfaction. As a well-established company with extensive experience in Power Trading, Shakthi Power offers each customer an innovative solution concept that is tailored to the customer's individual requirements.<br><br>
    We hereby share our commercial offer for the quantum required by you and we would be very happy to hear from you for your suggestions.
  </div>

  <div class="section-heading">Please find below the Pricing:</div>
  {{PRICING_TABLE}}

  <div class="pricing-note">
    Price is inclusive of Transmission charges, system operation, scheduling Charges, transmission losses,
    cross-subsidy surcharge, Wheeling charges, DSM Charges, AMR readings, Import energy charges and additional surcharges.
  </div>

  ${proposal.powerFacilitationCharge != null ? `
  <div class="facilitation">
    Power facilitation charges of Rs. ${proposal.powerFacilitationCharge}/- per unit plus applicable ${proposal.gstOnFacilitationCharge || 18}% GST are exclusive of the above tariff.
  </div>` : ''}

  <div class="terms-title">Terms and Conditions:</div>
  <div class="term-row"><span class="term-label">Contract Period</span><span>: ${proposal.contractTerms?.contractPeriod || 3} years</span></div>
  <div class="term-row"><span class="term-label">Lock-in period</span><span>: ${proposal.contractTerms?.lockInPeriod || 2} Years</span></div>
  <div class="term-row"><span class="term-label">Notice Period</span><span>: ${proposal.contractTerms?.noticePeriod || 6} Months after the lock-in period</span></div>
  <div class="term-row"><span class="term-label">Commencement</span><span>: From the ${proposal.contractTerms?.commencementMonth || '{Supply Month}'} billing cycle.</span></div>

  <div class="sub-head">A) Billing:</div>
  <div class="bullet">• The monthly bill will be raised based on the Energy Allocation statement certified and issued by TANGEDCO.</div>

  <div class="sub-head">B) Payment:</div>
  <div class="bullet">• Due date: Payment to be made within 5-6 working days from the date of Invoice.</div>

  <div class="sub-head">C) Payment Security Mechanism:</div>
  <div class="bullet">• Not Applicable</div>

  <div class="sub-head">D) Rate Revision:</div>
  <div class="bullet">• TNEB base tariff and any open access charges revision will be shared between the Consumer and power supplier on a 50-50% basis.</div>

  <div class="sub-head">E) Minimum assured supply per annum:</div>
  <div class="bullet">• ${proposal.billingTerms?.minimumAssuredSupply || '80 to 85%'} of the contracted quantum</div>

  <div class="sub-head">F) Required documents</div>
  <div class="bullet">• Last 3 Months TNEB Bill copies, 0.2s calibration certificate &amp; if required NOC from EDC</div>
  <div class="bullet">• PAN card copy, GST Registration, Incorporation certificate, AOA &amp; MOA, Consent Letter &amp; Authorisation letter</div>

  <div class="sub-head">G) Documents to be Signed with Supplier</div>
  <div class="bullet">• Power Sale Agreement (PSA)/ TERM Sheet</div>
  <div class="bullet">• Power Facilitation agreement</div>

  <div class="sub-head">H) Energy Supply &amp; Adjustments</div>
  <div class="bullet">• Peak hour generation shall be adjusted against peak hour consumption;</div>
  <div class="bullet">• Normal hour generation shall be adjusted against normal hour consumption and excess generation shall be adjusted against Off-peak hour consumption.</div>
  <div class="bullet">• After DSM implementation, the energy supply and adjustment will be on as per the relevant orders by TNERC.</div>

  <div class="validity-box">
    This offer is valid only till <u>${validityDate}</u>; after that, the availability of the power and the terms and prices are subject to change.
  </div>

  <div class="closing">
    Once again, thanks for the opportunity and looking forward to your confirmation to proceed further to enable the TANTRANSCO approvals.
  </div>`

  // Commercial Terms tab fields — resolved to their proposal values below and
  // usable in the Proposal Body / Terms & Conditions as {FIELD NAME} tokens
  // (e.g. {MINIMUM ASSURED SUPPLY}). The old {{camelCase}} form still works
  // too, so templates typed before this format existed keep resolving.
  const commercialFieldTokens: [string, string][] = [
    ['CONTRACT PERIOD',             String(proposal.contractTerms?.contractPeriod ?? '')],
    ['LOCK IN PERIOD',              String(proposal.contractTerms?.lockInPeriod ?? '')],
    ['NOTICE PERIOD',               String(proposal.contractTerms?.noticePeriod ?? '')],
    ['COMMENCEMENT MONTH',          proposal.contractTerms?.commencementMonth || ''],
    ['VALIDITY',                    String(proposal.validity ?? '')],
    ['COMMERCIAL TERMS',            proposal.commercialTerms || ''],
    ['POWER FACILITATION CHARGE',   proposal.powerFacilitationCharge != null ? String(proposal.powerFacilitationCharge) : ''],
    ['GST ON FACILITATION CHARGE',  String(proposal.gstOnFacilitationCharge ?? 18)],
    ['PAYMENT DUE IN DAYS',         String(proposal.billingTerms?.paymentDueInDays ?? '')],
    ['MINIMUM ASSURED SUPPLY',      proposal.billingTerms?.minimumAssuredSupply || ''],
    ['RATE REVISION',               proposal.billingTerms?.rateRevision || ''],
    ['BILL RAISE METHOD',           proposal.billingTerms?.billRaiseMethod || ''],
  ]
  const escapeForRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Fills in {{placeholders}} / {FIELD NAME} shared by the letter body and the
  // Terms & Conditions — both are manager-authored CKEditor content that can
  // reference the same tokens.
  const applyPlaceholders = (html: string): string => {
    let out = html
      .replace(/\{\{\s*PRICING_TABLE\s*\}\}/g, pricingTableOnly)
      .replace(/\{\{\s*validityDate\s*\}\}/gi, validityDate)
      .replace(/\{VALIDITY_DATE\}/g, validityDate)
      .replace(/\{\{\s*customerName\s*\}\}/g, customerName || '')
      .replace(/\{\{\s*customerCity\s*\}\}/g, customerCity || '')
      .replace(/\{\{\s*customerAddress\s*\}\}/g, customerAddress || '')
      .replace(/\{\{\s*contractPeriod\s*\}\}/g, String(proposal.contractTerms?.contractPeriod ?? ''))
      .replace(/\{\{\s*lockInPeriod\s*\}\}/g, String(proposal.contractTerms?.lockInPeriod ?? ''))
      .replace(/\{\{\s*noticePeriod\s*\}\}/g, String(proposal.contractTerms?.noticePeriod ?? ''))
      .replace(/\{\{\s*commencementMonth\s*\}\}/gi, proposal.contractTerms?.commencementMonth || '')
      .replace(/\{\{\s*powerFacilitationCharge\s*\}\}/gi, proposal.powerFacilitationCharge != null ? String(proposal.powerFacilitationCharge) : '')
      .replace(/\{\{\s*gstOnFacilitationCharge\s*\}\}/gi, String(proposal.gstOnFacilitationCharge ?? 18))
      .replace(/\{\{\s*commercialTerms\s*\}\}/gi, proposal.commercialTerms || '')
      .replace(/\{\{\s*validity\s*\}\}/gi, String(proposal.validity ?? ''))
      .replace(/\{\{\s*minimumAssuredSupply\s*\}\}/gi, proposal.billingTerms?.minimumAssuredSupply || '')
      .replace(/\{\{\s*rateRevision\s*\}\}/gi, proposal.billingTerms?.rateRevision || '')
      .replace(/\{\{\s*billRaiseMethod\s*\}\}/gi, proposal.billingTerms?.billRaiseMethod || '')
      .replace(/\{\{\s*paymentDueInDays\s*\}\}/gi, String(proposal.billingTerms?.paymentDueInDays ?? ''))
      .replace(/\{\{\s*deliveredTariff\s*\}\}/g, '')
      .replace(/\{\{\s*refNo\s*\}\}/g, proposal.referenceNumber || '')

    commercialFieldTokens.forEach(([label, value]) => {
      out = out.replace(new RegExp(`\\{\\s*${escapeForRegex(label)}\\s*\\}`, 'gi'), value)
    })
    return out
  }

  /**
   * Strip every inline font-family / font-size / text-color / highlight
   * declaration that CKEditor injects (its fontColor and fontBackgroundColor
   * toolbar buttons emit inline `color:`/`background-color:` on whatever text
   * was selected). This is the only reliable way to achieve a uniform,
   * professional look across all rich-text zones — CSS !important can override
   * stylesheet rules but NOT inline styles, and letting each paragraph/heading
   * keep whatever size/color someone picked while typing is what made past
   * proposals look inconsistent (mismatched sizes, random orange/blue text).
   *
   * Strategy: regex-replace occurrences of `font-family:...` / `font-size:...`
   * / `color:...` / `background-color:...` inside any style="..." attribute,
   * and remove any <style> blocks that contain @font-face or the same
   * declarations injected by the editor or paste-from-Word. Stripped text then
   * falls back to the page stylesheet's own (consistent) sizing/coloring for
   * its zone — see .letter-body etc. below.
   */
  const stripInlineFonts = (html: string): string => {
    const props = ['font-family', 'font-size']
    let out = html
    for (const prop of props) {
      // Remove the declaration from inline style attributes
      out = out.replace(new RegExp(`(style\\s*=\\s*["'][^"']*)${prop}\\s*:[^;}"']+;?\\s*`, 'gi'), '$1')
    }
    // Remove font-face / size blocks from any embedded <style> tags
    out = out.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, (block) => {
      let b = block.replace(/@font-face\s*\{[^}]*\}/gi, '')
      for (const prop of props) b = b.replace(new RegExp(`${prop}\\s*:[^;{}"']+;`, 'gi'), '')
      return b
    })
    return out
  }

  const cleanRichTextHtml = (html: string): string => {
    let out = stripInlineFonts(html)
    out = out.replace(/(?:<p>\s*(?:&nbsp;|<br\s*\/?>|\s*)*<\/p>|<br\s*\/?>|\s)+$/gi, '')
    return out
  }

  // If a template body is supplied, use it (with placeholders filled in).
  const rawBody = templateBodyHtml && templateBodyHtml.trim() ? templateBodyHtml : defaultBody
  const bodyHtml = cleanRichTextHtml(applyPlaceholders(rawBody))


  // ── Dynamic Cover Page (page 1) — full bleed, no orange panel ──
  const hasCover = true
  const bgImg = coverPage?.image || 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&w=1200&q=80'
  const coverTitle = coverPage?.title || 'COMMERCIAL PROPOSAL'
  const coverSubtitle = coverPage?.subtitle || 'SOLAR ENERGY FOR A BETTER TOMORROW'

  const coverLeftStyle = `background-image: url('${bgImg}'); background-size:cover; background-position:center;`

  const coverPageHtml = `
  <div class="page cover-page">
    <div class="cover-left" style="${coverLeftStyle}">
      <!-- top banner with logo + slogan -->
      <div class="cover-banner">
        <div class="cover-banner-logo">
          <img src="${logoBase64}" alt="Shakthi Power Logo" />
        </div>
        <div class="cover-banner-slogan">${coverSubtitle}</div>
      </div>
      <!-- centered title overlay -->
      <div class="cover-overlay">
        <div class="cover-overlay-title">${coverTitle}</div>
        <div class="cover-overlay-divider"></div>
        <div class="cover-overlay-meta">
          <div><span class="cover-meta-label">Prepared For</span><br><span class="cover-meta-value">${customerName || '—'}</span></div>
          <div style="margin-top:14px"><span class="cover-meta-label">Reference No</span><br><span class="cover-meta-value">${proposal.referenceNumber || '—'}</span></div>
          <div style="margin-top:14px"><span class="cover-meta-label">Date</span><br><span class="cover-meta-value">${offerDate}</span></div>
        </div>
      </div>
      <!-- bottom author strip -->
      <div class="cover-footer-strip">
        <div class="cover-footer-name">${preparedBy || 'Shakthi Power'}</div>
        <div class="cover-footer-company">Grand Shakthi Power P Ltd &nbsp;|&nbsp; info@shakthipower.com</div>
      </div>
    </div>
  </div>`

  // ── Company Content page (page 2) ──
  const hasCompany = !!(companyContentHtml && companyContentHtml.trim())
  const companyPageHtml = hasCompany ? `
  <div class="page company-page page-break-before">
    <div class="company-content">${cleanRichTextHtml(companyContentHtml)}</div>
  </div>` : ''

  // ── Terms & Conditions (page 4) ──
  const hasTerms = !!(termsConditionsHtml && termsConditionsHtml.trim())
  const termsHtml = hasTerms ? `
  <div class="terms-title">Terms and Conditions:</div>
  <div class="terms-content">${cleanRichTextHtml(applyPlaceholders(termsConditionsHtml!))}</div>` : ''

  const mainPageClass = `page main-page page-break-before`

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Proposal - ${proposal.id}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    @page{margin:0;size:A4 portrait;}
    @media print{
      html, body{
        background:white !important;
        width:210mm !important;
        margin:0 !important;
        padding:0 !important;
        -webkit-print-color-adjust:exact !important;
        print-color-adjust:exact !important;
      }
      .page{
        box-shadow:none !important;
        margin:0 !important;
        width:210mm !important;
        height:auto !important;
        min-height:0 !important;
        max-height:none !important;
        padding:12mm 15mm 10mm 15mm !important;
        page-break-inside:auto !important;
        break-inside:auto !important;
      }
      .cover-page{
        padding:0 !important;
        margin:0 !important;
        width:210mm !important;
        height:297mm !important;
        min-height:297mm !important;
        max-height:297mm !important;
        page-break-before:avoid !important;
        break-before:avoid !important;
        page-break-after:always !important;
        break-after:page !important;
        -webkit-print-color-adjust:exact !important;
        print-color-adjust:exact !important;
      }
      .cover-page + .page-break-before,
      .cover-page + .company-page,
      .cover-page + .main-page {
        page-break-before:avoid !important;
        break-before:avoid !important;
      }
      .page-break-before{
        page-break-before:always !important;
        break-before:page !important;
      }
      .section-heading, .sub-head, .terms-title, h1, h2, h3 {
        page-break-after:avoid !important;
        break-after:avoid !important;
      }
      table, .pricing, .facilitation, .validity-box, .sig-section, .ref-row, .header-top {
        page-break-inside:avoid !important;
        break-inside:avoid !important;
      }
      .bullet, p, .term-row {
        orphans:3;
        widows:3;
      }
    }
    body{font-family:'Inter','Segoe UI',Arial,sans-serif;color:#000;background:#e8e8e8;}
    .page{
      width:210mm;min-height:297mm;
      margin:10px auto;padding:15mm 18mm 12mm 18mm;
      background:white;box-shadow:0 0 10px rgba(0,0,0,0.2);
    }
    .header-top{
      display:flex;justify-content:space-between;align-items:flex-start;
      border-bottom:2px solid #001A4D;padding-bottom:8px;margin-bottom:10px;
    }
    .header-logo img{height:60px;width:auto;}
    .header-addr{text-align:right;font-size:10px;color:#333;line-height:1.5;}
    .header-addr p{margin:0;}
    .ref-row{display:flex;justify-content:space-between;font-size:12px;font-weight:bold;margin-bottom:18px;color:#1e293b;}
    .to-section{font-size:12.5px;line-height:1.8;margin-bottom:16px;}
    .to-section .cust-name{font-weight:bold;}
    .intro{font-size:12px;text-align:justify;line-height:1.65;color:#333;margin-bottom:14px;}
    .section-heading{font-size:13px;font-weight:bold;color:#001A4D;margin-bottom:8px;}
    table.pricing{width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:8px;}
    table.pricing th{
      background:#001A4D;color:#fff;padding:9px 8px;text-align:center;
      border:1px solid #000;font-weight:bold;
    }
    .pricing-note{font-size:11.5px;margin-bottom:8px;text-align:justify;line-height:1.55;color:#333;}
    .facilitation{
      background:#fffbe6;border:1px solid #ffe58f;padding:8px 10px;
      font-size:11.5px;font-weight:bold;margin-bottom:14px;
    }
    .terms-title{font-size:13px;font-weight:bold;color:#001A4D;margin-bottom:10px;}
    .term-row{font-size:11.5px;margin-bottom:6px;display:flex;gap:8px;}
    .term-label{font-weight:bold;min-width:140px;}
    .sub-head{font-weight:bold;color:#00A651;font-size:12px;margin-top:12px;margin-bottom:4px;}
    .bullet{font-size:11.5px;margin-left:16px;margin-bottom:4px;text-align:justify;line-height:1.55;}
    .validity-box{
      background:#fffbe6;border:1px solid #ffe58f;padding:8px 10px;
      font-size:11.5px;font-weight:bold;margin:14px 0;
    }
    .closing{font-size:12px;margin-bottom:12px;text-align:justify;}
    .sig-section{margin-top:20px;font-size:12px;}
    .footer{
      border-top:2px solid #001A4D;padding-top:7px;margin-top:20px;
      font-size:9.5px;color:#333;text-align:center;line-height:1.5;
    }
    .page-break-before{page-break-before:always;}
    /* ── Full-bleed cover page (no orange side panel) ── */
    .cover-page{
      width:210mm;min-height:297mm;max-height:297mm;
      margin:10px auto;padding:0 !important;
      background:white;box-shadow:0 0 10px rgba(0,0,0,0.2);
      overflow:hidden;position:relative;
      -webkit-print-color-adjust:exact;print-color-adjust:exact;
    }
    .cover-left{
      width:100%;height:100%;min-height:297mm;position:relative;
      background-size:cover;background-position:center;background-repeat:no-repeat;
      display:flex;flex-direction:column;
      -webkit-print-color-adjust:exact;print-color-adjust:exact;
    }
    /* dark overlay so text is readable over any background image */
    .cover-left::after{
      content:'';position:absolute;inset:0;
      background:linear-gradient(180deg,rgba(0,0,0,0.35) 0%,rgba(0,0,0,0.55) 100%);
      pointer-events:none;
    }
    .cover-banner{
      position:relative;z-index:2;
      width:100%;background:rgba(255,255,255,0.95);
      padding:14px 24px;display:flex;align-items:center;
      justify-content:space-between;gap:12px;
      border-bottom:1px solid rgba(0,0,0,0.1);
      box-shadow:0 2px 12px rgba(0,0,0,0.15);
    }
    .cover-banner-logo img{height:44px;width:auto;max-width:160px;object-fit:contain;}
    .cover-banner-slogan{
      font-size:13px;font-weight:800;color:#00A651;
      text-transform:uppercase;letter-spacing:0.5px;text-align:right;line-height:1.3;
    }
    /* centre overlay card */
    .cover-overlay{
      position:relative;z-index:2;
      flex:1;display:flex;flex-direction:column;
      justify-content:center;align-items:flex-start;
      padding:0 52px;
    }
    .cover-overlay-title{
      font-size:34px;font-weight:900;color:#fff;
      text-transform:uppercase;letter-spacing:2px;line-height:1.2;
      text-shadow:0 2px 8px rgba(0,0,0,0.5);
    }
    .cover-overlay-divider{
      width:80px;height:4px;background:#00A651;
      margin:18px 0;
    }
    .cover-overlay-meta{color:#f1f5f9;font-size:14px;line-height:2;}
    .cover-meta-label{
      font-size:10px;text-transform:uppercase;
      letter-spacing:1px;color:#94a3b8;
    }
    .cover-meta-value{font-weight:700;font-size:15px;color:#fff;}
    /* bottom author strip */
    .cover-footer-strip{
      position:relative;z-index:2;
      width:100%;background:rgba(0,26,77,0.85);
      padding:14px 28px;
      border-top:3px solid #00A651;
    }
    .cover-footer-name{
      font-size:14px;font-weight:700;color:#fff;
    }
    .cover-footer-company{
      font-size:11px;color:#94a3b8;margin-top:2px;
    }
    .company-page{
      padding:15mm 18mm 12mm 18mm !important;
    }
    .company-page .company-content{
      font-size:11.5px !important;
      line-height:1.5 !important;
      color:#333;
      text-align:justify;
    }
    .company-page .company-content p{margin-bottom:6px !important;}
    .company-page .company-content h1,.company-page .company-content h2,.company-page .company-content h3{
      color:#001A4D;margin:10px 0 6px !important;
    }
    .company-page .company-content ul,.company-page .company-content ol{margin:0 0 6px 18px !important;}
    .company-page .company-content li{margin-bottom:3px !important;}
    .company-page .company-content img{max-width:100%;height:auto;}
    .company-page .company-content table{border-collapse:collapse;width:100%;margin-bottom:10px;}
    .company-page .company-content th,.company-page .company-content td{border:1px solid #ccc;padding:6px 8px;}
    /* Baseline for the letter body when it's freeform CKEditor content (not the
       predefined .intro/.bullet/etc. classes) — keeps custom template text the
       same size/rhythm as the rest of the document instead of falling back to
       the browser's much-larger default paragraph size. */
    .letter-body{font-size:12px;line-height:1.65;color:#333;text-align:justify;}
    .letter-body p{margin-bottom:10px;}
    .letter-body h1,.letter-body h2,.letter-body h3{font-size:13px;color:#001A4D;margin:12px 0 6px;}
    .letter-body ul,.letter-body ol{margin:0 0 8px 18px;}
    .letter-body li{margin-bottom:4px;}
    .letter-body table{border-collapse:collapse;width:100%;margin-bottom:8px;}
    .letter-body th,.letter-body td{border:1px solid #ccc;padding:6px 8px;}
    .terms-content{font-size:11px;line-height:1.45;color:#333;text-align:justify;}
    .terms-content p{margin-bottom:6px;}
    .terms-content ul,.terms-content ol{margin:0 0 6px 18px;}
    .terms-content li{margin-bottom:3px;}
    .terms-content table{border-collapse:collapse;width:100%;margin-bottom:8px;}
    .terms-content th,.terms-content td{border:1px solid #ccc;padding:6px 8px;}
    /* Enforce a single high-quality font-family across the entire document
       including tables, paragraphs, and lists copied from external documents. */
    *, *::before, *::after,
    body, p, span, td, th, h1, h2, h3, div,
    .cover-page .cover-description, .cover-page .cover-description *,
    .company-page .company-content, .company-page .company-content *,
    .letter-body, .letter-body *,
    .terms-content, .terms-content * {
      font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif !important;
    }
  </style>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
</head>
<body>
${coverPageHtml}
${companyPageHtml}

<div class="${mainPageClass}">

  <!-- HEADER -->
  <div class="header-top">
    <div class="header-logo">
      <img src="${logoBase64}" alt="Shakthi Power Logo" />
    </div>
    <div class="header-addr">
      <p>No 35-B, 2<sup>nd</sup> Floor, Mantralayam Towers, Dr Rajendra Prasad Road,</p>
      <p>Hasthinapuram, Chrompet, Chennai - 600 044.</p>
      <p>Contact: 044 22239575 &nbsp;&nbsp; mail : info@shakthipower.com</p>
      <p><strong>www.power.com</strong></p>
    </div>
  </div>

  <!-- REF & DATE -->
  <div class="ref-row">
    <span>Ref No : <u>${proposal.referenceNumber || 'SP/2026-27/XXXX RE2-TPP Offer'}</u></span>
    <span>Offer Date : ${offerDate}</span>
  </div>

  <!-- CUSTOMER -->
  <div class="to-section">
    To,<br>
    <span class="cust-name">${customerName}</span><br>
    ${customerAddress},<br>
    ${customerCity}.
  </div>

  <!-- BODY (from chosen / default Proposal Template, else built-in letter) -->
  <div class="letter-body">${bodyHtml}</div>

  <!-- SIGNATURE (if no separate T&C page) -->
  ${!hasTerms ? `
  <div class="sig-section" style="margin-top: 30px;">
    <div>Thanking You</div>
    <div style="margin-top:6px;font-weight:bold;">For SHAKTHI POWER</div>
    ${preparedBy ? `
    <div style="margin-top:28px;display:flex;justify-content:space-between;align-items:flex-end;">
      <div>
        <div style="border-top:1.5px solid #001A4D;width:160px;margin-bottom:4px;"></div>
        <div style="font-size:11px;font-weight:700;color:#001A4D;">${preparedBy}</div>
        <div style="font-size:10px;color:#475569;margin-top:1px;">Prepared By</div>
      </div>
      <div style="text-align:right;">
        <div style="border-top:1.5px solid #001A4D;width:160px;margin-bottom:4px;"></div>
        <div style="font-size:11px;font-weight:700;color:#001A4D;">Authorised Signatory</div>
        <div style="font-size:10px;color:#475569;margin-top:1px;">For Shakthi Power</div>
      </div>
    </div>` : `
    <div style="margin-top:28px;">
      <div style="border-top:1.5px solid #001A4D;width:160px;margin-bottom:4px;"></div>
      <div style="font-size:11px;font-weight:700;color:#001A4D;">Authorised Signatory</div>
    </div>`}
  </div>
  ` : ''}

  <!-- FOOTER -->
  <div class="footer">
    No 35-B, 2<sup>nd</sup> Floor, Mantralayam Towers, Dr Rajendra Prasad Road, Hasthinapuram, Chrompet<br>
    Chennai - 600 044. &nbsp;&nbsp; Contact: 044 22239575 &nbsp;&nbsp; mail : info@shakthipower.com
  </div>

</div>

<!-- PAGE 4: Terms and Conditions & Signatures -->
${hasTerms ? `
<div class="page page-break-before">
  <!-- HEADER -->
  <div class="header-top" style="margin-bottom: 20px;">
    <div class="header-logo">
      <img src="${logoBase64}" alt="Shakthi Power Logo" />
    </div>
    <div class="header-addr">
      <p>No 35-B, 2<sup>nd</sup> Floor, Mantralayam Towers, Dr Rajendra Prasad Road,</p>
      <p>Hasthinapuram, Chrompet, Chennai - 600 044.</p>
      <p>Contact: 044 22239575 &nbsp;&nbsp; mail : info@shakthipower.com</p>
      <p><strong>www.power.com</strong></p>
    </div>
  </div>

  <div style="margin-top:10px; min-height: 120mm;">
    ${termsHtml}
  </div>

  <!-- SIGNATURE -->
  <div class="sig-section" style="margin-top: 25px;">
    <div>Thanking You</div>
    <div style="margin-top:6px;font-weight:bold;">For SHAKTHI POWER</div>
    ${preparedBy ? `
    <div style="margin-top:28px;display:flex;justify-content:space-between;align-items:flex-end;">
      <div>
        <div style="border-top:1.5px solid #001A4D;width:160px;margin-bottom:4px;"></div>
        <div style="font-size:11px;font-weight:700;color:#001A4D;">${preparedBy}</div>
        <div style="font-size:10px;color:#475569;margin-top:1px;">Prepared By</div>
      </div>
      <div style="text-align:right;">
        <div style="border-top:1.5px solid #001A4D;width:160px;margin-bottom:4px;"></div>
        <div style="font-size:11px;font-weight:700;color:#001A4D;">Authorised Signatory</div>
        <div style="font-size:10px;color:#475569;margin-top:1px;">For Shakthi Power</div>
      </div>
    </div>` : `
    <div style="margin-top:28px;">
      <div style="border-top:1.5px solid #001A4D;width:160px;margin-bottom:4px;"></div>
      <div style="font-size:11px;font-weight:700;color:#001A4D;">Authorised Signatory</div>
    </div>`}
  </div>

  <!-- FOOTER -->
  <div class="footer">
    No 35-B, 2<sup>nd</sup> Floor, Mantralayam Towers, Dr Rajendra Prasad Road, Hasthinapuram, Chrompet<br>
    Chennai - 600 044. &nbsp;&nbsp; Contact: 044 22239575 &nbsp;&nbsp; mail : info@shakthipower.com
  </div>

</div>
` : ''}
</body>
</html>`
}

/**
 * Directly download the proposal as a PDF by opening the HTML in a new
 * browser tab and triggering print-to-PDF (Save as PDF) from there.
 * The @page{margin:0} CSS we inject suppresses browser URL/date headers.
 */
export const printProposalPDF = (htmlContent: string, filename?: string) => {
  const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' })
  const url  = URL.createObjectURL(blob)

  // Open in a hidden iframe so the user doesn't see a flash
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
  document.body.appendChild(iframe)

  const doc = iframe.contentWindow?.document
  if (!doc) {
    // Fallback: new tab with print triggered automatically
    const w = window.open('', '_blank')
    if (w) {
      w.document.write(htmlContent)
      w.document.close()
      w.focus()
      setTimeout(() => w.print(), 900)
    }
    URL.revokeObjectURL(url)
    return
  }

  doc.open()
  doc.write(htmlContent)
  doc.close()

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      setTimeout(() => {
        document.body.removeChild(iframe)
        URL.revokeObjectURL(url)
      }, 2500)
    }, 700)
  }
}

